// Editorial Rural-Tech — Theme controller (light/dark, system-aware, persisted)
import { useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "er-theme"; // 'light' | 'dark' | 'system'

function resolveSystem() {
  if (typeof window === "undefined") return "light";
  return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyTheme(theme) {
  if (typeof document === "undefined") return;
  const effective = theme === "system" ? resolveSystem() : theme;
  const root = document.documentElement;
  if (effective === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
  root.style.colorScheme = effective;
}

export function initTheme() {
  if (typeof window === "undefined") return;
  const saved = localStorage.getItem(STORAGE_KEY) || "system";
  applyTheme(saved);
}

export function useTheme() {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === "undefined") return "system";
    return localStorage.getItem(STORAGE_KEY) || "system";
  });
  const [resolved, setResolved] = useState(() => (theme === "system" ? resolveSystem() : theme));

  useEffect(() => {
    applyTheme(theme);
    setResolved(theme === "system" ? resolveSystem() : theme);
  }, [theme]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      if ((localStorage.getItem(STORAGE_KEY) || "system") === "system") {
        applyTheme("system");
        setResolved(resolveSystem());
      }
    };
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  const setTheme = useCallback((next) => {
    localStorage.setItem(STORAGE_KEY, next);
    setThemeState(next);
  }, []);

  const toggle = useCallback(() => {
    const current = (localStorage.getItem(STORAGE_KEY) || "system");
    const effective = current === "system" ? resolveSystem() : current;
    setTheme(effective === "dark" ? "light" : "dark");
  }, [setTheme]);

  return { theme, resolved, setTheme, toggle };
}
